import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useTheme } from "styled-components";
import { toast } from "react-toastify";
import api from "../../services/api";
import { Container, ModalRoot } from "./styles";
import UsersLoadingSkeleton from "./UsersLoadingSkeleton";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
  FiUsers,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiMail,
  FiActivity,
  FiClock,
  FiUserPlus,
  FiUserX,
  FiLogIn,
  FiSearch,
  FiShield,
  FiCheckCircle,
} from "react-icons/fi";
import {
  GoPersonFill,
} from "react-icons/go";
import { SYSTEM_MODULES } from "../../config/modules";

export default function Users() {
  const theme = useTheme();
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editTab, setEditTab] = useState("info");
  const [newUserStep, setNewUserStep] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [newUser, setNewUser] = useState({ email: "" });
  const [newUserPermissions, setNewUserPermissions] = useState({});
  const [inviting, setInviting] = useState(false);
  const [editUserPermissions, setEditUserPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [emailValidating, setEmailValidating] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [currentProjectPermissions, setCurrentProjectPermissions] = useState(null);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedExistingUser, setSelectedExistingUser] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeout = useRef(null);

  // Função para buscar usuários do projeto selecionado
  const fetchUsers = async () => {
    const selectedProject = localStorage.getItem("vidal_selected_project");

    if (!selectedProject) {
      setError("Projeto não selecionado");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await api.get(`/users/project/${selectedProject}`);
      
      
      const usersList = response.data.users || [];
      
      
      const formattedUsers = usersList.map((user) => ({
        id: user._id || user.id,
        name: user.name,
        email: user.email,
        role: user.cargo,
        status: "active",
        color: user.color,
        projetos: user.projetos,
        permissions: user.permissions || {},
        lastAccess: user.lastAccess,
      }));
      
      
      setUsers(formattedUsers);
    } catch (err) {
      console.error("Erro ao buscar usuários:", err);
      setError("Erro ao carregar usuários do projeto");
    } finally {
      setLoading(false);
    }
  };

  // Buscar permissões do projeto atual (localStorage + fallback API)
  useEffect(() => {
    const loadProjectPermissions = async () => {
      // 1) Tenta do localStorage
      const projectData = localStorage.getItem('vidal_current_project_data');
      let perms = null;
      if (projectData) {
        try {
          const project = JSON.parse(projectData);
          if (project.permissions && Object.keys(project.permissions).length > 0) {
            perms = project.permissions;
          }
        } catch (e) {
          console.error('Erro ao parsear dados do projeto:', e);
        }
      }

      // 2) Fallback: busca da API se não veio permissions no localStorage
      if (!perms) {
        const projectId = localStorage.getItem('vidal_selected_project');
        if (projectId) {
          try {
            const response = await api.get(`/projects/${projectId}`);
            const project = response.data?.project || response.data;
            perms = project?.permissions || {};
          } catch (e) {
            console.error('Erro ao buscar permissões do projeto:', e);
            perms = {};
          }
        } else {
          perms = {};
        }
      }

      setCurrentProjectPermissions(perms);
    };

    loadProjectPermissions();
  }, []);

  // Buscar usuários ao carregar o componente
  useEffect(() => {
    fetchUsers();
  }, []);

  // Cleanup do timeout de busca ao desmontar
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Dados mockados de logs de eventos
  const activityLogs = [
    {
      id: 1,
      user: "João Silva",
      action: "login",
      description: "Fez login no sistema",
      timestamp: "2024-12-11T10:30:00",
    },
    {
      id: 2,
      user: "Maria Santos",
      action: "create",
      description: "Criou novo usuário: Pedro Costa",
      timestamp: "2024-12-11T09:15:00",
    },
    {
      id: 3,
      user: "João Silva",
      action: "edit",
      description: "Editou permissões de Ana Oliveira",
      timestamp: "2024-12-11T08:45:00",
    },
    {
      id: 4,
      user: "Pedro Costa",
      action: "login",
      description: "Fez login no sistema",
      timestamp: "2024-12-10T16:20:00",
    },
    {
      id: 5,
      user: "Ana Oliveira",
      action: "delete",
      description: "Removeu usuário: Carlos Mendes",
      timestamp: "2024-12-10T14:10:00",
    },
    {
      id: 6,
      user: "Maria Santos",
      action: "login",
      description: "Fez login no sistema",
      timestamp: "2024-12-10T11:30:00",
    },
  ];

  const getUserInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  // Paleta de cores comerciais vibrantes com bom contraste
  const commercialColors = [
    '#3b82f6', // Azul vibrante
    '#10b981', // Verde
    '#f59e0b', // Laranja
    '#8b5cf6', // Roxo
    '#ef4444', // Vermelho
    '#06b6d4', // Ciano
    '#ec4899', // Rosa
    '#14b8a6', // Teal
    '#6366f1', // Índigo
    '#f97316', // Laranja escuro
    '#a855f7', // Roxo claro
    '#0ea5e9', // Azul céu
    '#84cc16', // Lima
    '#f43f5e', // Rosa vermelho
    '#8b5cf6', // Violeta
    '#06b6d4', // Sky
  ];

  // Obter cor única para cada usuário baseado no índice na lista filtrada
  const getUserColor = (userId, index) => {
    // Se temos um índice, usar ele para garantir cores únicas na lista
    if (index !== undefined) {
      return commercialColors[index % commercialColors.length];
    }
    
    // Fallback: usar hash do userId para consistência
    let hash = 0;
    const id = userId?.toString() || '0';
    for (let i = 0; i < id.length; i++) {
      hash = id.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return commercialColors[Math.abs(hash) % commercialColors.length];
  };

  // Verificar se usuário tem acesso total aos módulos disponíveis no projeto
  const hasFullAccess = (permissions) => {
    if (!permissions) return false;
    const availableModules = getAvailableModules();
    if (availableModules.length === 0) return false;
    return availableModules.every(module => 
      permissions[module.key] && permissions[module.key] !== "none"
    );
  };

  // Módulos disponíveis para conceder ao usuário = SUBCONJUNTO dos módulos
  // habilitados no projeto. Oculta os que o projeto não habilitou.
  const getAvailableModules = () =>
    SYSTEM_MODULES
      .filter(m => currentProjectPermissions?.[m.id] === true)
      .map(m => ({ key: m.id, label: m.label, icon: m.icon, color: m.color, section: m.section }));

  const getRoleLabel = (role) => {
    const roles = {
      admin: "Administrador",
      user: "Usuário",
      viewer: "Visualizador",
    };
    return roles[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: "#ef4444",
      user: "#10b981",
      viewer: "#3b82f6",
    };
    return colors[role] || "#6b7280";
  };

  const getActionIcon = (action) => {
    const icons = {
      login: <FiLogIn />,
      create: <FiUserPlus />,
      edit: <FiEdit2 />,
      delete: <FiUserX />,
    };
    return icons[action] || <FiActivity />;
  };

  const getActionColor = (action) => {
    const colors = {
      login: "#3b82f6",
      create: "#10b981",
      edit: "#f59e0b",
      delete: "#ef4444",
    };
    return colors[action] || "#6b7280";
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (minutes < 60) {
      return `${minutes} min atrás`;
    } else if (hours < 24) {
      return `${hours}h atrás`;
    } else {
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "short",
      });
    }
  };

  const searchAvailableUsers = async (email) => {
    if (!email || email.length < 3) {
      setSuggestedUsers([]);
      setShowSuggestions(false);
      return;
    }

    setSearchingUsers(true);
    setEmailError("");

    try {
      const selectedProject = localStorage.getItem("vidal_selected_project");
      const response = await api.get('/users/search', {
        params: {
          projectId: selectedProject,
          q: email,
        },
      });

      const usersList = response.data.users || [];
      setSuggestedUsers(usersList);
      setShowSuggestions(usersList.length > 0);
    } catch (error) {
      console.error("Erro ao buscar usuários disponíveis:", error);
      setSuggestedUsers([]);
      setShowSuggestions(false);
    } finally {
      setSearchingUsers(false);
    }
  };

  const checkEmailExists = async (email) => {
    if (!email || !email.includes('@')) {
      setEmailError("");
      return false;
    }

    setEmailValidating(true);
    setEmailError("");

    try {
      const selectedProject = localStorage.getItem("vidal_selected_project");
      const response = await api.get(`/users/project/${selectedProject}`);

      const usersList = response.data.users || [];
      const emailExists = usersList.some(
        user => user.email && user.email.toLowerCase() === email.toLowerCase()
      );

      if (emailExists) {
        setEmailError("Este email já está cadastrado");
        return true;
      }

      return false;
    } catch (error) {
      console.error("Erro ao verificar email:", error);
      return false;
    } finally {
      setEmailValidating(false);
    }
  };

  const handleEmailChange = (value) => {
    setNewUser({ ...newUser, email: value });
    setSelectedExistingUser(null);
    setEmailError("");

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      searchAvailableUsers(value);
    }, 300);
  };

  const handleSelectExistingUser = (user) => {
    setSelectedExistingUser(user);
    setNewUser({ ...newUser, email: user.email });
    setSuggestedUsers([]);
    setShowSuggestions(false);
  };

  const handleInviteUser = async () => {
    setError("");
    const selectedProject = localStorage.getItem("vidal_selected_project");

    if (!newUser.email) {
      toast.error("Por favor, informe o email do convidado");
      return;
    }

    setInviting(true);
    const loadingToast = toast.loading("Enviando convite...");

    try {
      const response = await api.post("/users/invite", {
        email: newUser.email,
        permissions: newUserPermissions,
        projeto: selectedProject,
      });

      if (response.data.success) {
        toast.update(loadingToast, {
          render: "Convite enviado com sucesso!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });

        handleCloseNewUserModal();
      }
    } catch (error) {
      console.error("Erro ao enviar convite:", error);
      const errorMessage = error.response?.data?.error || "Erro ao enviar convite";

      toast.update(loadingToast, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });

      setError(errorMessage);
    } finally {
      setInviting(false);
    }
  };

  const handleAddExistingUser = async () => {
    setError("");
    const selectedProject = localStorage.getItem("vidal_selected_project");

    if (!selectedExistingUser || !selectedExistingUser.id) {
      toast.error("Por favor, selecione um usuário existente");
      return;
    }

    setInviting(true);
    const loadingToast = toast.loading("Adicionando usuário ao projeto...");

    try {
      const response = await api.post("/users/add-project", {
        userId: selectedExistingUser.id,
        projectId: selectedProject,
        role: 'user',
        permissions: newUserPermissions,
      });

      if (response.data.success) {
        toast.update(loadingToast, {
          render: "Usuário adicionado ao projeto com sucesso!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });

        handleCloseNewUserModal();
        await fetchUsers();
      }
    } catch (error) {
      console.error("Erro ao adicionar usuário ao projeto:", error);
      const errorMessage = error.response?.data?.error || "Erro ao adicionar usuário ao projeto";

      toast.update(loadingToast, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });

      setError(errorMessage);
    } finally {
      setInviting(false);
    }
  };

  const handleCloseNewUserModal = () => {
    setShowNewUserModal(false);
    setNewUser({ email: "" });
    setNewUserStep(1);
    setNewUserPermissions({});
    setSelectedExistingUser(null);
    setSuggestedUsers([]);
    setShowSuggestions(false);
    setEmailError("");
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setShowDeleteDialog(false);
    const selectedProject = localStorage.getItem("vidal_selected_project");
    const loadingToast = toast.loading("Deletando usuário...");

    try {
      await api.delete(`/users/${userToDelete.id}`, {
        data: {
          projectId: selectedProject,
        },
      });
      
      toast.update(loadingToast, {
        render: "Usuário deletado com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      
      await fetchUsers();
    } catch (error) {
      console.error("Erro ao deletar usuário:", error);
      const errorMessage = error.response?.data?.error || "Erro ao deletar usuário";
      
      toast.update(loadingToast, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      
      setError(errorMessage);
    } finally {
      setUserToDelete(null);
    }
  };

  const handleEditUser = async (user) => {
    setEditingUser({ ...user });
    setEditTab("info");
    setEditUserPermissions(user.permissions || {});
    setShowEditUserModal(true);
  };

  const handleSaveEdit = async () => {
    setError("");
    if (!editingUser) return;

    try {
      const response = await api.put(`/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        cargo: editingUser.role,
        color: editingUser.color,
        permissions: editingUser.role === 'admin' ? {} : editUserPermissions,
      });

      toast.success("Usuário atualizado com sucesso!");
      setShowEditUserModal(false);
      setEditingUser(null);
      
      // Recarregar lista de usuários
      await fetchUsers();
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);
      setError(error.response?.data?.error || "Erro ao atualizar usuário");
    } finally {
      setLoading(false);
    }
  };


  // Filtrar usuários
  const filteredUsers = users.filter((user) => {
    // Não mostrar usuários god na lista
    if (user.role === "god") return false;

    const matchSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchRole = roleFilter === "all" || user.role === roleFilter;

    return matchSearch && matchRole;
  });

  // Formatar data do último acesso
  const formatLastAccess = (dateStr) => {
    if (!dateStr) return "Nunca";
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const time = date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    if (isToday) return `Hoje às ${time}`;
    const datePart = date.toLocaleDateString("pt-BR");
    return `${datePart} às ${time}`;
  };

  // Mostrar skeleton durante loading
  if (loading) {
    return <UsersLoadingSkeleton isDark={theme.body !== "#fafbfc"} />;
  }

  return (
    <Container>
      {/* Header */}
      <div className="header-info">
        <div className="header-details">
          <h3>Gerenciamento de Usuários</h3>
          <div className="header-meta">
            <span className="status-tag">SISTEMA ATIVO</span>
            <span className="update-tag">
              {users.filter((u) => u.role !== "god").length > 0
                ? `${users.filter((u) => u.role !== "god").length} usuário${users.filter((u) => u.role !== "god").length !== 1 ? 's' : ''} cadastrado${users.filter((u) => u.role !== "god").length !== 1 ? 's' : ''}`
                : 'nenhum usuário cadastrado'}
            </span>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="header-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#10b98112', color: '#10b981' }}>
            <FiUsers />
          </div>
          <div>
            <div className="stat-value">{users.filter((u) => u.role !== "god").length}</div>
            <div className="stat-label">Total de Usuários</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#3b82f612', color: '#3b82f6' }}>
            <FiActivity />
          </div>
          <div>
            <div className="stat-value">
              {users.filter((u) => u.status === "active" && u.role !== "god").length}
            </div>
            <div className="stat-label">Usuários Ativos</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#ef444412', color: '#ef4444' }}>
            <FiUserX />
          </div>
          <div>
            <div className="stat-value">
              {users.filter((u) => u.status !== "active" && u.role !== "god").length}
            </div>
            <div className="stat-label">Usuários Inativos</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f59e0b12', color: '#f59e0b' }}>
            <FiShield />
          </div>
          <div>
            <div className="stat-value">
              {users.filter((u) => u.role === "admin").length}
            </div>
            <div className="stat-label">Administradores</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#8b5cf612', color: '#8b5cf6' }}>
            <GoPersonFill />
          </div>
          <div>
            <div className="stat-value">
              {users.filter((u) => u.role === "user").length}
            </div>
            <div className="stat-label">Usuários Comuns</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <button
            className={`filter-btn ${roleFilter === "all" ? "active" : ""}`}
            onClick={() => setRoleFilter("all")}
          >
            Todos
          </button>
          <button
            className={`filter-btn ${roleFilter === "admin" ? "active" : ""}`}
            onClick={() => setRoleFilter("admin")}
          >
            Administradores
          </button>
          <button
            className={`filter-btn ${roleFilter === "user" ? "active" : ""}`}
            onClick={() => setRoleFilter("user")}
          >
            Usuários
          </button>
          <div className="search-input">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar usuários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="toolbar-right">
          <button
            className="btn-new-user"
            onClick={() => setShowNewUserModal(true)}
          >
            <FiPlus />
            Novo Usuário
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Users List */}
        <div className="users-container">
          <div className="users-grid">
            {filteredUsers.map((user, index) => {
              
              return (
              <div key={user.id} className="user-card">
                <div className="user-card-header">
                  <div className="user-avatar">
                    <div className="avatar-circle" style={{ background: getUserColor(user.id, index) }}>
                      <p>{getUserInitials(user.name)}</p>
                    </div>
                  </div>
                  <div className="user-info">
                    <h5>{user.name}</h5>
                    <p className="user-email">
                      <FiMail />
                      {user.email}
                    </p>
                  </div>
                </div>
                <div className="user-card-body">
                  <div className="user-meta">
                    <span
                      className="role-badge"
                      style={{
                        background: `${getRoleColor(user.role)}20`,
                        color: getRoleColor(user.role),
                      }}
                    >
                      {getRoleLabel(user.role)}
                    </span>
                    <span className="last-access">
                      Último acesso:{" "}
                      {formatLastAccess(user.lastAccess)}
                    </span>
                  </div>

                  {/* Badges de Permissões */}
                  <div className="user-permissions-badges">
                    {user.role === 'admin' || hasFullAccess(user.permissions) ? (
                      <div className="full-access-badge">
                        <FiShield />
                        <span>Acesso total</span>
                      </div>
                    ) : (
                      <>
                        {getAvailableModules().map(module => {
                          const Icon = module.icon;
                          const hasAccess = user.permissions?.[module.key] && user.permissions[module.key] !== "none";
                          return (
                            <div
                              key={module.key}
                              className="badge"
                              data-access={String(hasAccess)}
                              data-color={module.color}
                              title={`${module.label}: ${user.permissions?.[module.key] || 'none'}`}
                            >
                              <Icon />
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
                <div className="user-card-footer">
                  <button
                    className="btn-edit"
                    title="Editar usuário"
                    onClick={() => handleEditUser(user)}
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="btn-delete"
                    title="Remover usuário"
                    onClick={() => handleDeleteUser(user)}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modal Convidar Usuário */}
      {showNewUserModal && ReactDOM.createPortal(
        <ModalRoot>
          <div
            className="modal-overlay"
            onClick={() => setShowNewUserModal(false)}
          />
          <div className="edit-user-modal invite-modal">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>{newUserStep === 1 ? 'Convidar Usuário' : 'Permissões de Acesso'}</h3>
                <p className="modal-subtitle">{newUserStep === 1 ? 'Envie um convite por email' : 'Defina o que o usuário poderá acessar'}</p>
              </div>
              <button
                className="close-btn"
                onClick={() => setShowNewUserModal(false)}
              >
                ×
              </button>
            </div>

            {/* Indicador de Etapa */}
            <div className="step-indicator">
              <div className={newUserStep === 1 ? "step active" : "step completed"}>
                <div className="step-number">1</div>
                <span>Email</span>
              </div>
              <div className="step-divider"></div>
              <div className={newUserStep === 2 ? "step active" : "step"}>
                <div className="step-number">2</div>
                <span>Permissões</span>
              </div>
            </div>

            <div className="modal-body">
              {/* Etapa 1: Email */}
              {newUserStep === 1 && (
                <div className="invite-step-email">
                  <div className="invite-hero">
                    <div className="invite-hero-icon">
                      <FiMail />
                    </div>
                    <p>Informe o email da pessoa que deseja convidar. Ela receberá um link para criar a conta com nome e senha.</p>
                  </div>
                  <div className="form-group">
                    <label>
                      <FiMail />
                      Email
                    </label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      onBlur={(e) => checkEmailExists(e.target.value)}
                      placeholder="usuario@empresa.com"
                      className={emailError ? "error" : ""}
                      autoFocus
                    />
                    {emailValidating && (
                      <span className="form-hint validating">
                        Verificando email...
                      </span>
                    )}
                    {emailError && (
                      <span className="form-hint error">
                        {emailError}
                      </span>
                    )}
                    {showSuggestions && (
                      <div className="suggestions-list">
                        {searchingUsers ? (
                          <div className="suggestions-loading">Buscando usuários...</div>
                        ) : suggestedUsers.length === 0 ? (
                          <div className="suggestions-empty">Nenhum usuário encontrado</div>
                        ) : (
                          <>
                            <div className="suggestions-header">
                              Usuários encontrados
                            </div>
                            {suggestedUsers.map((user) => (
                              <div
                                key={user.id}
                                className="suggestion-item"
                                onClick={() => {
                                  handleSelectExistingUser(user);
                                  setNewUserStep(2);
                                }}
                              >
                                <div className="user-avatar">
                                  {user.name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="user-info">
                                  <h4>{user.name}</h4>
                                  <p>{user.email}</p>
                                </div>
                                <div className="user-role">
                                  {getRoleLabel(user.cargo || user.role)}
                                </div>
                                <div className="check-icon">
                                  <FiCheckCircle />
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="invite-note">
                    <FiShield style={{ flexShrink: 0 }} />
                    <span>
                      {selectedExistingUser
                        ? `Adicionar ${selectedExistingUser.name} ao projeto. Defina as permissões na próxima etapa.`
                        : "O convite expira em 7 dias. O usuário terá acesso apenas aos módulos que você selecionar na próxima etapa."}
                    </span>
                  </div>
                  {error && <div className="error-message">{error}</div>}
                </div>
              )}

              {/* Etapa 2: Permissões Booleanas */}
              {newUserStep === 2 && (
                <div className="permissions-list">
                  <div className="permission-toggle-header">
                    <span>Módulo</span>
                    <button 
                      className="toggle-all-btn"
                      onClick={() => {
                        const allEnabled = getAvailableModules().every(m => newUserPermissions[m.key] === true);
                        const updated = { ...newUserPermissions };
                        getAvailableModules().forEach(m => { updated[m.key] = !allEnabled; });
                        setNewUserPermissions(updated);
                      }}
                    >
                      {getAvailableModules().every(m => newUserPermissions[m.key] === true) ? 'Desmarcar todos' : 'Marcar todos'}
                    </button>
                  </div>

                  {getAvailableModules().length === 0 ? (
                    <div className="empty-permissions">
                      <FiShield style={{ fontSize: '3em', opacity: 0.3 }} />
                      <p>Nenhum módulo disponível para este projeto</p>
                    </div>
                  ) : (
                    <>
                      {['dashboards', 'tools', 'settings'].map(section => {
                        const sectionModules = getAvailableModules().filter(m => m.section === section);
                        if (sectionModules.length === 0) return null;
                        const sectionLabel = section === 'dashboards' ? 'Dashboards' : section === 'tools' ? 'Ferramentas' : 'Configurações';
                        return (
                          <div key={section} className="permission-section">
                            <h5>{sectionLabel}</h5>
                            {sectionModules.map(module => {
                              const Icon = module.icon;
                              const isEnabled = newUserPermissions[module.key] === true;
                              return (
                                <div 
                                  key={module.key} 
                                  className={`permission-toggle-item ${isEnabled ? 'enabled' : ''}`}
                                  data-color={module.color}
                                  onClick={() => setNewUserPermissions({ ...newUserPermissions, [module.key]: !isEnabled })}
                                >
                                  <div className="permission-label">
                                    <Icon className="permission-icon" />
                                    <span>{module.label}</span>
                                  </div>
                                  <div className={`toggle-switch ${isEnabled ? 'on' : 'off'}`}>
                                    <div className="toggle-knob"></div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={handleCloseNewUserModal}
              >
                Cancelar
              </button>

              {newUserStep === 1 ? (
                <button
                  className="btn-next-step"
                  onClick={async () => {
                    const emailExists = await checkEmailExists(newUser.email);
                    if (!emailExists) {
                      setNewUserStep(2);
                    }
                  }}
                  disabled={
                    !newUser.email ||
                    emailValidating ||
                    !!emailError
                  }
                >
                  <FiShield />
                  <span>Próximo</span>
                </button>
              ) : (
                <>
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setNewUserStep(1);
                      setSelectedExistingUser(null);
                    }}
                  >
                    Voltar
                  </button>
                  <button
                    className="btn-create"
                    onClick={selectedExistingUser ? handleAddExistingUser : handleInviteUser}
                    disabled={inviting}
                  >
                    {inviting ? (
                      <>
                        <div className="spinner-small"></div>
                        {selectedExistingUser ? "Adicionando..." : "Enviando..."}
                      </>
                    ) : (
                      <>
                        {selectedExistingUser ? <FiUserPlus /> : <FiMail />}
                        {selectedExistingUser ? "Adicionar ao Projeto" : "Enviar Convite"}
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </ModalRoot>,
        document.body
      )}

      {/* Modal Editar Usuário */}
      {showEditUserModal && editingUser && ReactDOM.createPortal(
        <ModalRoot>
          <div
            className="modal-overlay"
            onClick={() => setShowEditUserModal(false)}
          />
          <div className="edit-user-modal">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>Editar Usuário</h3>
                <p className="modal-subtitle">Gerencie informações e permissões</p>
              </div>
              <button
                className="close-btn"
                onClick={() => setShowEditUserModal(false)}
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div className="modal-tabs">
              <button
                className={`tab ${editTab === "info" ? "active" : ""}`}
                onClick={() => setEditTab("info")}
              >
                Informações
              </button>
              <button
                className={`tab ${editTab === "access" ? "active" : ""}`}
                onClick={() => setEditTab("access")}
              >
                Acesso
              </button>
            </div>

            <div className="modal-body">
              {/* Perfil do Usuário */}
              <div className="user-profile">
                <div className="profile-thumb">
                  <div className="profile-circle" style={{ background: getUserColor(editingUser.id) }}>
                    <p>{getUserInitials(editingUser.name)}</p>
                  </div>
                </div>
                <div className="profile-info">
                  <h4>{editingUser.name}</h4>
                  <p className="profile-email">{editingUser.email}</p>

                  {/* Badges de Permissões */}
                  <div className="permission-badges">
                    {editingUser.role === 'admin' || hasFullAccess(editUserPermissions) ? (
                      <div className="full-access-badge">
                        <FiShield />
                        <span>Acesso total</span>
                      </div>
                    ) : (
                      <>
                        {getAvailableModules().map(module => {
                          const Icon = module.icon;
                          const hasAccess = editUserPermissions[module.key] && editUserPermissions[module.key] !== "none";
                          return (
                            <div
                              key={module.key}
                              className="badge"
                              data-access={String(hasAccess)}
                              data-color={module.color}
                              title={`${module.label}: ${editUserPermissions[module.key]}`}
                            >
                              <Icon />
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Tab Informações */}
              {editTab === "info" && (
                <>
                  <div className="form-group">
                    <label>Nome Completo</label>
                    <input
                      type="text"
                      value={editingUser.name}
                      onChange={(e) =>
                        setEditingUser({ ...editingUser, name: e.target.value })
                      }
                      placeholder="Digite o nome completo"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={editingUser.email}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          email: e.target.value,
                        })
                      }
                      placeholder="Digite o email"
                    />
                  </div>
                  <div className="form-group">
                    <label>Função</label>
                    <select
                      value={editingUser.role}
                      onChange={(e) =>
                        setEditingUser({ ...editingUser, role: e.target.value })
                      }
                    >
                      <option value="viewer">Visualizador</option>
                      <option value="user">Usuário</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Nova Senha</label>
                    <input
                      type="password"
                      placeholder="Digite uma nova senha (opcional)"
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirmar Senha</label>
                    <input
                      type="password"
                      placeholder="Confirme a nova senha"
                    />
                  </div>
                </>
              )}

              {/* Tab Acesso - Permissões Booleanas */}
              {editTab === "access" && (
                <>
                  {editingUser.role === 'admin' ? (
                    <div className="admin-access-message">
                      <FiShield />
                      <p>
                        Administradores herdam automaticamente todas as permissões ativas no projeto.
                      </p>
                    </div>
                  ) : (
                    <div className="permissions-list">
                      <div className="permission-toggle-header">
                        <span>Módulo</span>
                        <button 
                          className="toggle-all-btn"
                          onClick={() => {
                            const allEnabled = getAvailableModules().every(m => editUserPermissions[m.key] === true);
                            const updated = { ...editUserPermissions };
                            getAvailableModules().forEach(m => { updated[m.key] = !allEnabled; });
                            setEditUserPermissions(updated);
                          }}
                        >
                          {getAvailableModules().every(m => editUserPermissions[m.key] === true) ? 'Desmarcar todos' : 'Marcar todos'}
                        </button>
                      </div>

                      {getAvailableModules().length === 0 ? (
                        <div className="empty-permissions">
                          <FiShield style={{ fontSize: '3em', opacity: 0.3 }} />
                          <p>Nenhum módulo disponível para este projeto</p>
                        </div>
                      ) : (
                        <>
                          {['dashboards', 'tools', 'settings'].map(section => {
                            const sectionModules = getAvailableModules().filter(m => m.section === section);
                            if (sectionModules.length === 0) return null;
                            const sectionLabel = section === 'dashboards' ? 'Dashboards' : section === 'tools' ? 'Ferramentas' : 'Configurações';
                            return (
                              <div key={section} className="permission-section">
                                <h5>{sectionLabel}</h5>
                                {sectionModules.map(module => {
                                  const Icon = module.icon;
                                  const isEnabled = editUserPermissions[module.key] === true || editUserPermissions[module.key] === 'edit' || editUserPermissions[module.key] === 'admin' || editUserPermissions[module.key] === 'view';
                                  return (
                                    <div 
                                      key={module.key} 
                                      className={`permission-toggle-item ${isEnabled ? 'enabled' : ''}`}
                                      data-color={module.color}
                                      onClick={() => setEditUserPermissions({ ...editUserPermissions, [module.key]: !isEnabled })}
                                    >
                                      <div className="permission-label">
                                        <Icon className="permission-icon" />
                                        <span>{module.label}</span>
                                      </div>
                                      <div className={`toggle-switch ${isEnabled ? 'on' : 'off'}`}>
                                        <div className="toggle-knob"></div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowEditUserModal(false)}
              >
                Cancelar
              </button>
              <button className="btn-save" onClick={handleSaveEdit}>
                Salvar Alterações
              </button>
            </div>
          </div>
        </ModalRoot>,
        document.body
      )}

      {/* Dialog de Confirmação de Exclusão */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDeleteUser}
        title="Excluir Usuário"
        message={`Tem certeza que deseja excluir o usuário "${userToDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </Container>
  );
}
